import { ref, onMounted, onUnmounted, unref } from "vue";

/**
 * 懒加载 composable（一次性：进入视口后保持可见）
 */
export function useLazyLoad(options = {}) {
  const { root = null, rootMargin = "50px", threshold = 0.01 } = options;

  const targetRef = ref(null);
  const isVisible = ref(false);
  let observer = null;

  onMounted(() => {
    if (!targetRef.value) return;

    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isVisible.value) {
            isVisible.value = true;
            if (observer && targetRef.value) {
              observer.unobserve(targetRef.value);
            }
          }
        });
      },
      {
        root: unref(root) ?? null,
        rootMargin,
        threshold,
      }
    );

    observer.observe(targetRef.value);
  });

  onUnmounted(() => {
    observer?.disconnect();
    observer = null;
  });

  return {
    targetRef,
    isVisible,
  };
}

/**
 * 视口检测 composable（双向：离开视口时 isInViewport 会变为 false）
 * 适用于滚动容器内的动图/视频，离开视口时卸载以停止解码
 */
export function useInViewport(options = {}) {
  const { root = null, rootMargin = "200px 0px", threshold = 0 } = options;

  const targetRef = ref(null);
  const isInViewport = ref(false);
  let observer = null;

  onMounted(() => {
    if (!targetRef.value) return;

    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target === targetRef.value) {
            isInViewport.value = entry.isIntersecting;
          }
        });
      },
      {
        root: unref(root) ?? null,
        rootMargin,
        threshold,
      }
    );

    observer.observe(targetRef.value);
  });

  onUnmounted(() => {
    observer?.disconnect();
    observer = null;
  });

  return {
    targetRef,
    isInViewport,
  };
}
